import { useCallback } from 'react';
import { useEgClickstream } from '@shared-ui/clickstream-analytics-context';
import { useStores } from 'utility/hooks';
import { LoadedFilterPresentationType } from 'types/search/sort-filter';
import { FlightsFilterItemAnalyticsFragment } from '__generated__/api';
import { validateFilterAnalytics } from 'utility';

type EventContext = {
  // eslint-disable-next-line camelcase
  search_request: {
    filters: [Omit<FlightsFilterItemAnalyticsFragment, '__typename'>];
  };
};

export const useTrackEvent = (eventType?: string) => {
  const track = useEgClickstream();
  const { context: pageContext, sortFilterStore } = useStores();
  const filterPresentation = sortFilterStore.sortAndFilterResult?.filterPresentation as LoadedFilterPresentationType;
  const { eventAnalytics } = filterPresentation || {};
  const { pageName, eventCategory } = eventAnalytics || {};

  const traceId = pageContext['traceInfo']?.['Trace-ID'] ?? '';

  return useCallback(
    (eventName: string, eventContext: EventContext) =>
      track({
        schema: {
          // eslint-disable-next-line camelcase
          schema_name: 'DomainEvent'
        },
        event: {
          // eslint-disable-next-line camelcase
          event_name: eventName,
          // eslint-disable-next-line camelcase
          event_type: eventType,
          // eslint-disable-next-line camelcase
          event_category: eventCategory,
          // eslint-disable-next-line camelcase
          page_name: pageName,
          // eslint-disable-next-line camelcase
          clickstream_trace_id: traceId
        },
        eventContext
      }),
    [pageName, eventCategory, eventType, traceId, track]
  );
};

export const useTrackInteractionEvent = () => {
  const { sortFilterStore } = useStores();
  const filterPresentation = sortFilterStore.sortAndFilterResult?.filterPresentation as LoadedFilterPresentationType;
  const { eventAnalytics } = filterPresentation || {};
  const { eventType } = eventAnalytics || {};

  return useTrackEvent(eventType);
};

export const useFilterTrackEvent = () => {
  const trackEvent = useTrackInteractionEvent();
  const { sortFilterStore } = useStores();
  const filterPresentation = sortFilterStore.sortAndFilterResult?.filterPresentation as LoadedFilterPresentationType;
  const { eventAnalytics } = filterPresentation || {};
  const { filterRemovedEventName, filterAppliedEventName } = eventAnalytics || {};

  return useCallback(
    (filterItemAnalytics, isApplied?: boolean) => {
      const eventName = isApplied ? filterAppliedEventName : filterRemovedEventName;
      validateFilterAnalytics(filterItemAnalytics) &&
        trackEvent(eventName, {
          // eslint-disable-next-line camelcase
          search_request: {
            filters: [filterItemAnalytics]
          }
        });
    },
    [filterAppliedEventName, filterRemovedEventName, trackEvent]
  );
};
